"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Award, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const entranceMotion = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 15
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  const floatMotion = shouldReduceMotion ? {} : {
    animate: {
      y: [0, -6, 0]
    },
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };
  return <section id="herointro" data-nav="light" className="relative w-full overflow-hidden bg-background pt-28 pb-20 md:pt-36 md:pb-28 lg:pt-40 lg:pb-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: 5 Cols on desktop */}
          <motion.div {...entranceMotion} className="flex flex-col items-start lg:col-span-5">
            {/* Craftsmanship Badge */}
            <div className="mb-6 flex items-center gap-2">
              <Badge variant="outline" className="rounded-full border-primary/30 bg-primary/10 px-3.5 py-1 text-xs font-semibold tracking-wider text-primary uppercase">
                <Sparkles className="mr-1.5 size-3.5 text-primary" />
                Brookhaven, GA
              </Badge>
            </div>

            {/* Primary Headline */}
            <h1 className="font-serif text-4xl leading-[1.12] font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Kitchen remodeling in{" "}
              <span className="italic font-normal">Brookhaven, GA</span>
            </h1>

            {/* Brushed Gold Accent Hairline */}
            <div className="mt-5 mb-6 h-[1.5px] w-20 bg-primary/60" />

            {/* Lead Narrative Paragraph */}
            <p className="max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Plan your kitchen with custom white oak cabinetry, honed
              quartzite islands, and layout planning designed around your daily
              routines. From historic Brookhaven bungalows to new builds along
              Peachtree Dunwoody, we handle every permit, milestone, and finish.
            </p>

            {/* Credential Check Strip */}
            <div className="mt-6 flex items-center gap-2 text-sm text-foreground">
              <CheckCircle2 className="size-4 text-primary" />
              <span>Fixed-scope estimates</span>
            </div>

            {/* Actions: Primary CTA and Secondary Link */}
            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="h-12 rounded-lg border border-primary/80 bg-primary px-7 text-sm font-semibold tracking-wide text-primary-foreground shadow-sm transition-[color,background-color,border-color,box-shadow] hover:bg-primary/90 hover:shadow-md">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an estimate
                </Link>
              </Button>

              <Link to="/OurProjectsPage" className="group inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground transition-colors hover:text-primary">
                <span>View project gallery</span>
                <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </motion.div>

          {/* Right Column: 7 Cols on desktop */}
          <motion.div {...entranceMotion} transition={{
          duration: 0.5,
          delay: shouldReduceMotion ? 0 : 0.15,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative lg:col-span-7">
            {/* Visual Frame */}
            <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-muted shadow-xl">
              <div className="relative aspect-[4/3] w-full sm:aspect-[16/11]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_remodel_hero.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_remodel_hero.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_remodel_hero.webp-wvc-sizes" alt="Bespoke kitchen remodel in Brookhaven featuring white oak cabinetry, honed quartzite waterfall island, and unlacquered brass fixtures" className="h-full w-full object-cover object-center" loading="eager" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              </div>
            </div>

            {/* Floating Atelier Frosted Glass Plinth Card */}
            <motion.div {...floatMotion} className="mt-4 sm:absolute sm:-bottom-8 sm:left-6 sm:mt-0 sm:max-w-xs md:left-8">
              <Card className="rounded-2xl border border-white/80 bg-white/85 p-5 shadow-xl backdrop-blur-md dark:border-border/60 dark:bg-card/90">
                <div className="flex items-start gap-3.5">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                    <Award className="size-5" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold tracking-wider text-primary uppercase">
                      Verified Standard
                    </span>
                    <h2 className="mt-0.5 font-serif text-base font-semibold text-foreground">
                      Artisan millwork & finish
                    </h2>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      Full permit oversight across Brookhaven and North Atlanta
                      residential building codes.
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}